import {
  Component,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  inject,
  input,
  signal,
  effect,
  computed,
  viewChild,
  output,
} from '@angular/core';
import { Popover } from 'primeng/popover';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Bind } from 'primeng/bind';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';

export interface ItemType {
  label: string;
  value?: string;
  group?: string;
}

@Component({
  selector: 'ag1-dropdown-button',
  templateUrl: './ag1-dropdown-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Ag1ButtonComponent,
    Ag1LoaderComponent,
    AngularSvgIconModule,
    Bind,
    Popover,
    HumanizePipe,
  ],
})
export class Ag1DropdownButtonComponent {
  private cdr = inject(ChangeDetectorRef);

  // Functionality to still add:
  // Allow user to navigate with keyboard (tabs and key presses) to select an item
  // Add aria-labels to dropdown and items
  // Add Humanize Pipe to Labels

  readonly variant = input<'primary' | 'secondary' | 'tertiary'>('primary');

  readonly disabled = input<boolean>(undefined);

  readonly isLoading = input<boolean>(false);

  readonly label = input<string>('Download');

  readonly items = input<ItemType[]>([]);

  readonly sortedItems = signal<ItemType[]>([]);

  groupedItems: { [key: string]: ItemType[] } = {};

  isOpen: boolean = false;

  readonly clickEvent = output<ItemType>();

  readonly dropdownPopover = viewChild.required<Popover>('dropdownPopover');

  constructor() {
    effect(() => {
      const items: ItemType[] = this.items();

      if (!items?.length) {
        this.sortedItems.set([]);
        this.groupedItems = {};
        return;
      }
      this.sortItems(items);
    });
  }

  onDropdownTriggerClick(event: Event): void {
    if (this.disabled() || this.sortedItems().length === 0) {
      return;
    }
    this.dropdownPopover().toggle(event);
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  sortItems(items: ItemType[]): void {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const sorted: ItemType[] = [...items].sort(
      (a: ItemType, b: ItemType) =>
        collator.compare(a?.group, b?.group) ||
        collator.compare(a.label, b.label),
    );
    this.sortedItems.set(sorted);

    if (!this.isGrouped()) {
      this.groupedItems = {};
      return;
    }

    this.groupedItems = sorted.reduce((obj, item) => {
      // if for some reason the item doesn't have a group, add it to 'Other' group
      if (!item?.group) {
        const fallbackGroup = 'Other';
        if (!obj[fallbackGroup]) {
          // Initialize and add item in one go
          obj[fallbackGroup] = [item];
        } else {
          // The group exists, so just push the item
          obj[fallbackGroup].push(item);
        }
        return obj;
      }

      const groupKey: string = item.group;
      if (!obj[groupKey]) {
        // Initialize and add item in one go
        obj[groupKey] = [item];
      } else {
        // The group exists, so just push the item
        obj[groupKey].push(item);
      }

      return obj;
    }, {});
  }

  readonly isGrouped = computed(() =>
    this.sortedItems().some((item) =>
      Object.prototype.hasOwnProperty.call(item, 'group'),
    ),
  );

  get groupKeys(): string[] {
    return Object.keys(this.groupedItems);
  }

  onClick(selectedItem: ItemType): void {
    if (!selectedItem) {
      return;
    }

    this.dropdownPopover().hide();
    this.clickEvent.emit(selectedItem);
  }
}
